import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { appEnv } from '~/config'
import {
  useLimitedFeatureAvailability,
  type Feature,
} from '~/domain/featureAvailability/useLimitedFeatureAvailability'
import { routes } from './paths'

type Props = {
  feature: Feature
  children: ReactNode
}

/**
 * 機能の利用可否 (`useLimitedFeatureAvailability`) を見て、'yes' のときだけ
 * 子ルートを描画する。'no' の場合はホームへリダイレクト。'unknown' (取得中)
 * の場合は描画を保留する。
 *
 * 旧 `FeatureFlagRoute` + `ContractPlanRoute` のネストを置き換える単一の
 * ルートガード。判定条件 (プラン / ロール / Flag) は catalog (`settings`)
 * に一元定義する。
 *
 * NOTE: ローカル開発環境では Feature Flag 起因の 'no' のみリダイレクトを
 * バイパスする (旧 `FeatureFlagRoute` と同じ挙動)。Plan / Role 起因の 'no'
 * は dev でも常にリダイレクトする (旧 `ContractPlanRoute` と同じ挙動)。
 * catalog 本体には bypass を入れず、UI 表示制御 (例: ハンバーガーメニューの
 * Language 項目) は実フラグ通りに動作させる。
 */
export function FeatureAvailabilityRoute({ feature, children }: Props) {
  const { planRole, flag } =
    useLimitedFeatureAvailability().getFeatureAvailabilityDetail(feature)

  if (planRole === 'no') {
    return <Navigate to={routes.home.buildPath({})} replace />
  }
  if (planRole === 'unknown') {
    return null
  }

  if (flag === 'no' && !appEnv.isLocal) {
    return <Navigate to={routes.home.buildPath({})} replace />
  }
  if (flag === 'unknown') {
    return null
  }

  return children
}
